import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      locations: 'Locations',
      quote: 'Get Quote',
      about: 'About',
      contact: 'Contact',
      blog: 'Blog',
      payment: 'Pay Invoice'
    },
    hero: {
      tagline: 'The Nervous System of Modern Business',
      headline: 'Low Voltage Infrastructure',
      subheadline: 'for Florida',
      description: 'Professional fiber optic, CCTV, networking, and security solutions. Serving Orlando, Miami, Tampa, and beyond.',
      cta: 'Get Quote',
      ctaSecondary: 'Our Services'
    },
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive low voltage solutions for your business',
      viewAll: 'View All Services',
      learnMore: 'Learn More'
    },
    locations: {
      title: 'Serving Florida',
      subtitle: 'Professional low voltage services across the Sunshine State'
    },
    quote: {
      title: 'Get Your Quote',
      subtitle: 'Tell us about your project and get an instant estimate',
      step1: 'Contact Info',
      step2: 'Services',
      step3: 'Project Details',
      step4: 'Review & Submit',
      name: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      city: 'City',
      address: 'Project Address',
      projectType: 'Project Type',
      commercial: 'Commercial',
      residential: 'Residential',
      industrial: 'Industrial',
      timeline: 'Timeline',
      urgent: 'Urgent (< 2 weeks)',
      standard: 'Standard (2-4 weeks)',
      flexible: 'Flexible',
      budget: 'Budget Range',
      notes: 'Additional Notes',
      uploadFiles: 'Upload Files (PDF, Images, KMZ)',
      submit: 'Submit Quote Request',
      estimatedTotal: 'Estimated Total',
      next: 'Next',
      back: 'Back',
      success: 'Quote submitted! We\'ll contact you within 24 hours.'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team',
      subject: 'Subject',
      message: 'Message',
      preferredContact: 'Preferred Contact Method',
      emailMethod: 'Email',
      phoneMethod: 'Phone',
      send: 'Send Message',
      success: 'Message sent successfully!'
    },
    about: {
      title: 'About IQES',
      subtitle: 'Your trusted partner in low voltage solutions',
      mission: 'Our Mission',
      missionText: 'Empowering Florida\'s infrastructure with next-generation low voltage solutions.',
      whyUs: 'Why Choose Us',
      experience: 'Years of Experience',
      projects: 'Projects Completed',
      clients: 'Happy Clients',
      certified: 'Certified Technicians'
    },
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Trusted by businesses across Florida'
    },
    cta: {
      title: 'Ready to Get Started?',
      subtitle: 'Contact us today for a free consultation',
      button: 'Get Quote'
    },
    footer: {
      description: 'Professional low voltage solutions for Florida businesses.',
      quickLinks: 'Quick Links',
      services: 'Services',
      locations: 'Service Areas',
      contact: 'Contact',
      rights: 'All rights reserved.'
    },
    chat: {
      title: 'IQES Assistant',
      placeholder: 'Type your message...',
      send: 'Send',
      greeting: 'Hi! How can I help you today?'
    }
  },
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      locations: 'Ubicaciones',
      quote: 'Cotización',
      about: 'Nosotros',
      contact: 'Contacto',
      blog: 'Blog',
      payment: 'Pagar Factura'
    },
    hero: {
      tagline: 'El Sistema Nervioso del Negocio Moderno',
      headline: 'Infraestructura de Bajo Voltaje',
      subheadline: 'para Florida',
      description: 'Soluciones profesionales de fibra óptica, CCTV, redes y seguridad. Sirviendo Orlando, Miami, Tampa y más.',
      cta: 'Cotización',
      ctaSecondary: 'Nuestros Servicios'
    },
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Soluciones integrales de bajo voltaje para su negocio',
      viewAll: 'Ver Todos los Servicios',
      learnMore: 'Más Información'
    },
    locations: {
      title: 'Sirviendo Florida',
      subtitle: 'Servicios profesionales de bajo voltaje en todo el Estado del Sol'
    },
    quote: {
      title: 'Obtenga Su Cotización',
      subtitle: 'Cuéntenos sobre su proyecto y obtenga una estimación instantánea',
      step1: 'Información de Contacto',
      step2: 'Servicios',
      step3: 'Detalles del Proyecto',
      step4: 'Revisar y Enviar',
      name: 'Nombre Completo',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      company: 'Empresa',
      city: 'Ciudad',
      address: 'Dirección del Proyecto',
      projectType: 'Tipo de Proyecto',
      commercial: 'Comercial',
      residential: 'Residencial',
      industrial: 'Industrial',
      timeline: 'Plazo',
      urgent: 'Urgente (< 2 semanas)',
      standard: 'Estándar (2-4 semanas)',
      flexible: 'Flexible',
      budget: 'Rango de Presupuesto',
      notes: 'Notas Adicionales',
      uploadFiles: 'Subir Archivos (PDF, Imágenes, KMZ)',
      submit: 'Enviar Solicitud de Cotización',
      estimatedTotal: 'Total Estimado',
      next: 'Siguiente',
      back: 'Atrás',
      success: '¡Cotización enviada! Le contactaremos en 24 horas.'
    },
    contact: {
      title: 'Contáctenos',
      subtitle: 'Póngase en contacto con nuestro equipo',
      subject: 'Asunto',
      message: 'Mensaje',
      preferredContact: 'Método de Contacto Preferido',
      emailMethod: 'Correo',
      phoneMethod: 'Teléfono',
      send: 'Enviar Mensaje',
      success: '¡Mensaje enviado exitosamente!'
    },
    about: {
      title: 'Sobre IQES',
      subtitle: 'Su socio de confianza en soluciones de bajo voltaje',
      mission: 'Nuestra Misión',
      missionText: 'Potenciando la infraestructura de Florida con soluciones de bajo voltaje de próxima generación.',
      whyUs: 'Por Qué Elegirnos',
      experience: 'Años de Experiencia',
      projects: 'Proyectos Completados',
      clients: 'Clientes Satisfechos',
      certified: 'Técnicos Certificados'
    },
    testimonials: {
      title: 'Lo Que Dicen Nuestros Clientes',
      subtitle: 'Confianza de empresas en toda Florida'
    },
    cta: {
      title: '¿Listo para Comenzar?',
      subtitle: 'Contáctenos hoy para una consulta gratuita',
      button: 'Cotización'
    },
    footer: {
      description: 'Soluciones profesionales de bajo voltaje para empresas de Florida.',
      quickLinks: 'Enlaces Rápidos',
      services: 'Servicios',
      locations: 'Áreas de Servicio',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.'
    },
    chat: {
      title: 'Asistente IQES',
      placeholder: 'Escribe tu mensaje...',
      send: 'Enviar',
      greeting: '¡Hola! ¿Cómo puedo ayudarte hoy?'
    }
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('iqes-language');
      return saved || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('iqes-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
